import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { Download, Loader2, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useDeleteAccount, useExportAccount, useSaveProfile, useOwnProfile } from '@/api/hooks';
import { DeleteAccountDialog } from './DeleteAccountDialog';
import { QRCodeDisplay } from './QRCodeDisplay';
import type { ContractorProfileInput } from '@/api/types';
import {
  loadContractorSettings,
  saveContractorSettings,
  loadLogo,
  saveLogo,
  removeLogo,
  resizeImage,
  type ContractorSettings,
} from '../lib/contractorSettings';

function selectOnFocus(e: React.FocusEvent<HTMLInputElement>): void {
  e.target.select();
}

interface ContractorSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ContractorSettingsDrawer({
  isOpen,
  onClose,
}: Readonly<ContractorSettingsDrawerProps>) {
  const { t, i18n } = useTranslation();
  const { getAccessToken, logout, user } = useKindeAuth();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  const tokenGetter = getAccessToken as () => Promise<string>;
  const deleteAccount = useDeleteAccount(tokenGetter);
  const exportAccount = useExportAccount(tokenGetter);
  const [settings, setSettings] = useState<ContractorSettings>(loadContractorSettings);
  const [markupStr, setMarkupStr] = useState('');
  const [paymentTermStr, setPaymentTermStr] = useState('');
  const [validityStr, setValidityStr] = useState('');
  const [vatRateStr, setVatRateStr] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [tagline, setTagline] = useState('');
  const [publicPhone, setPublicPhone] = useState('');
  const [publicEmail, setPublicEmail] = useState('');
  const [profilePublicId, setProfilePublicId] = useState('');
  const saveProfile = useSaveProfile(tokenGetter);
  const { data: existingProfile } = useOwnProfile(tokenGetter, user?.id);
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  useEffect(() => {
    if (isOpen) {
      const loaded = loadContractorSettings();
      setSettings(loaded);
      setMarkupStr(loaded.markupPercent === 0 ? '' : String(loaded.markupPercent));
      setPaymentTermStr(String(loaded.paymentTermDays));
      setValidityStr(String(loaded.validityDays));
      setVatRateStr(String(loaded.vatRate));
      setLogoDataUrl(loadLogo());
    }
  }, [isOpen]);

  useEffect(() => {
    if (existingProfile) {
      setTagline(existingProfile.tagline);
      setPublicPhone(existingProfile.phone);
      setPublicEmail(existingProfile.email);
      if (existingProfile.public_id) {
        setProfilePublicId(existingProfile.public_id);
      }
    }
  }, [existingProfile]);

  const updateField = useCallback((field: keyof ContractorSettings, value: string | number) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleExport = useCallback(() => {
    exportAccount.mutate(undefined, {
      onSuccess: (data) => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'voicequote-export.json';
        a.click();
        URL.revokeObjectURL(url);
        toast.success(t('account.exportSuccess'));
      },
      onError: () => {
        toast.error(t('account.exportFailed'));
      },
    });
  }, [exportAccount, t]);

  const handleDeleteAccount = useCallback(() => {
    deleteAccount.mutate(undefined, {
      onSuccess: () => {
        toast.success(t('account.deleteSuccess'));
        setShowDeleteDialog(false);
        logout();
      },
      onError: () => {
        toast.error(t('account.deleteFailed'));
      },
    });
  }, [deleteAccount, t, logout]);

  const handleLogoUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > 500 * 1024) {
        toast.error(t('contractor.logoTooLarge'));
        return;
      }

      if (!['image/png', 'image/jpeg'].includes(file.type)) {
        toast.error(t('contractor.logoInvalidFormat'));
        return;
      }

      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const dataUrl = reader.result as string;
          const resized = await resizeImage(dataUrl);
          saveLogo(resized);
          setLogoDataUrl(resized);
        } catch {
          toast.error(t('contractor.logoQuotaExceeded'));
        }
      };
      reader.readAsDataURL(file);
    },
    [t],
  );

  const handleRemoveLogo = useCallback(() => {
    removeLogo();
    setLogoDataUrl(null);
  }, []);

  const handleSave = useCallback(() => {
    const markupParsed = Number.parseFloat(markupStr);
    const paymentParsed = Number.parseInt(paymentTermStr, 10);
    const validityParsed = Number.parseInt(validityStr, 10);
    const vatRateParsed = Number.parseFloat(vatRateStr);
    const updatedSettings = {
      ...settings,
      markupPercent: Number.isNaN(markupParsed) ? 0 : markupParsed,
      paymentTermDays: Number.isNaN(paymentParsed) ? 14 : paymentParsed,
      validityDays: Number.isNaN(validityParsed) ? 30 : validityParsed,
      vatRate: Number.isNaN(vatRateParsed) ? 24 : vatRateParsed,
    };
    saveContractorSettings(updatedSettings);
    toast.success(t('contractor.saved'));
    const profileInput: ContractorProfileInput = {
      company_name: updatedSettings.companyName,
      tagline,
      phone: publicPhone,
      email: publicEmail,
      logo_url: logoDataUrl ?? '',
    };
    saveProfile.mutate(profileInput, {
      onSuccess: (saved) => {
        if (saved.public_id) {
          setProfilePublicId(saved.public_id);
        }
      },
      onError: () => {
        toast.error(t('profile.syncFailed'));
      },
    });
    // Note: onClose() is called BEFORE mutate completes (fire-and-forget pattern).
    // Profile sync is best-effort — localStorage save is the primary save, drawer closes immediately.
    onClose();
  }, [
    settings,
    markupStr,
    paymentTermStr,
    validityStr,
    vatRateStr,
    t,
    onClose,
    tagline,
    publicPhone,
    publicEmail,
    logoDataUrl,
    saveProfile,
  ]);

  if (!isOpen) return null;

  const header = (
    <div className="flex items-center justify-between border-b border-border px-4 py-3">
      <h2 className="text-lg font-semibold">{t('contractor.settings')}</h2>
      <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('quotes.closeDrawer')}>
        <X className="h-5 w-5" />
      </Button>
    </div>
  );

  const formContent = (
    <div className="space-y-4 p-4">
      <div>
        <label className="text-sm font-medium text-muted-foreground">{t('contractor.logo')}</label>
        {logoDataUrl ? (
          <div className="mt-2">
            <img
              src={logoDataUrl}
              alt={t('contractor.logo')}
              className="max-w-[120px] rounded border border-border"
            />
            <Button variant="outline" size="sm" onClick={handleRemoveLogo} className="mt-2">
              {t('contractor.removeLogo')}
            </Button>
          </div>
        ) : (
          <div className="mt-2">
            <label htmlFor="logo-upload">
              <Button variant="outline" size="sm" asChild>
                <span>
                  <Upload className="mr-1 h-4 w-4" />
                  {t('contractor.uploadLogo')}
                </span>
              </Button>
            </label>
            <input
              id="logo-upload"
              data-testid="logo-file-input"
              type="file"
              accept="image/png, image/jpeg"
              onChange={handleLogoUpload}
              className="hidden"
            />
          </div>
        )}
      </div>

      <div>
        <label className="text-sm font-medium text-muted-foreground">
          {t('requests.shareLink')}
        </label>
        <div className="mt-1 flex gap-2">
          <Input
            readOnly
            value={`${globalThis.location.origin}/?lang=${i18n.language}#request/${profilePublicId}`}
            className="flex-1 text-xs"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const url = `${globalThis.location.origin}/?lang=${i18n.language}#request/${profilePublicId}`;
              navigator.clipboard.writeText(url).then(() => {
                toast.success(t('requests.linkCopied'));
              });
            }}
          >
            {t('requests.copyLink')}
          </Button>
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-muted-foreground">{t('profile.qrCode')}</label>
        <div className="mt-2">
          <QRCodeDisplay
            url={`${globalThis.location.origin}/?lang=${i18n.language}#request/${profilePublicId}`}
          />
        </div>
      </div>

      <h3 className="text-sm font-semibold text-muted-foreground">{t('profile.publicProfile')}</h3>

      <div>
        <label htmlFor="profile-tagline" className="text-sm font-medium text-muted-foreground">
          {t('profile.tagline')}
        </label>
        <Input
          id="profile-tagline"
          type="text"
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          placeholder={t('profile.taglinePlaceholder')}
          maxLength={200}
          className="mt-1"
        />
      </div>

      <div>
        <label htmlFor="profile-public-phone" className="text-sm font-medium text-muted-foreground">
          {t('profile.publicPhone')}
        </label>
        <Input
          id="profile-public-phone"
          type="tel"
          value={publicPhone}
          onChange={(e) => setPublicPhone(e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <label htmlFor="profile-public-email" className="text-sm font-medium text-muted-foreground">
          {t('profile.publicEmail')}
        </label>
        <Input
          id="profile-public-email"
          type="email"
          value={publicEmail}
          onChange={(e) => setPublicEmail(e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <label htmlFor="contractor-company" className="text-sm font-medium text-muted-foreground">
          {t('contractor.companyName')}
        </label>
        <Input
          id="contractor-company"
          type="text"
          value={settings.companyName}
          onChange={(e) => updateField('companyName', e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <label htmlFor="contractor-address" className="text-sm font-medium text-muted-foreground">
          {t('contractor.address')}
        </label>
        <Input
          id="contractor-address"
          type="text"
          value={settings.address}
          onChange={(e) => updateField('address', e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <label htmlFor="contractor-regcode" className="text-sm font-medium text-muted-foreground">
          {t('contractor.regCode')}
        </label>
        <Input
          id="contractor-regcode"
          type="text"
          value={settings.regCode}
          onChange={(e) => updateField('regCode', e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <label htmlFor="contractor-vat" className="text-sm font-medium text-muted-foreground">
          {t('contractor.vatNumber')}
        </label>
        <Input
          id="contractor-vat"
          type="text"
          value={settings.vatNumber}
          onChange={(e) => {
            const value = e.target.value;
            updateField('vatNumber', value);
            if (value.trim() && (settings.vatRate === 22 || settings.vatRate === 0)) {
              updateField('vatRate', 24);
              setVatRateStr('24');
            }
          }}
          placeholder="EE123456789"
          className="mt-1"
        />
      </div>

      {settings.vatNumber.trim() !== '' && (
        <div>
          <label
            htmlFor="contractor-vat-rate"
            className="text-sm font-medium text-muted-foreground"
          >
            {t('contractor.vatRateLabel')}
          </label>
          <input
            id="contractor-vat-rate"
            type="text"
            inputMode="decimal"
            value={vatRateStr}
            onChange={(e) => setVatRateStr(e.target.value)}
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>
      )}

      <div>
        <label htmlFor="contractor-iban" className="text-sm font-medium text-muted-foreground">
          {t('contractor.iban')}
        </label>
        <Input
          id="contractor-iban"
          type="text"
          value={settings.iban}
          onChange={(e) => updateField('iban', e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <label
          htmlFor="contractor-payment-terms"
          className="text-sm font-medium text-muted-foreground"
        >
          {t('contractor.paymentTerms')}
        </label>
        <Input
          id="contractor-payment-terms"
          type="text"
          inputMode="numeric"
          value={paymentTermStr}
          placeholder="14"
          onFocus={selectOnFocus}
          onChange={(e) => setPaymentTermStr(e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <label
          htmlFor="contractor-validity-days"
          className="text-sm font-medium text-muted-foreground"
        >
          {t('contractor.validityDays')}
        </label>
        <Input
          id="contractor-validity-days"
          type="text"
          inputMode="numeric"
          value={validityStr}
          placeholder="30"
          onFocus={selectOnFocus}
          onChange={(e) => setValidityStr(e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <label htmlFor="contractor-markup" className="text-sm font-medium text-muted-foreground">
          {t('contractor.markupPercent')}
        </label>
        <Input
          id="contractor-markup"
          type="text"
          inputMode="decimal"
          value={markupStr}
          placeholder="0"
          onFocus={selectOnFocus}
          onChange={(e) => setMarkupStr(e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <label
          htmlFor="contractor-default-terms"
          className="text-sm font-medium text-muted-foreground"
        >
          {t('contractor.defaultTerms')}
        </label>
        <textarea
          id="contractor-default-terms"
          value={settings.defaultTerms}
          onChange={(e) => updateField('defaultTerms', e.target.value)}
          placeholder={t('contractor.defaultTermsPlaceholder')}
          rows={3}
          maxLength={2000}
          className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm resize-none"
        />
      </div>

      <Button onClick={handleSave} className="w-full">
        {t('contractor.save')}
      </Button>

      <hr className="border-border" />

      <h3 className="text-sm font-semibold text-muted-foreground">{t('account.title')}</h3>

      <Button
        variant="outline"
        className="w-full"
        onClick={handleExport}
        disabled={exportAccount.isPending}
        data-testid="export-data-button"
      >
        {exportAccount.isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('account.exporting')}
          </>
        ) : (
          <>
            <Download className="mr-2 h-4 w-4" />
            {t('account.exportData')}
          </>
        )}
      </Button>

      <Button
        variant="destructive"
        className="w-full"
        onClick={() => setShowDeleteDialog(true)}
        data-testid="delete-account-button"
      >
        <Trash2 className="mr-2 h-4 w-4" />
        {t('account.deleteAccount')}
      </Button>

      <DeleteAccountDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteAccount}
        isDeleting={deleteAccount.isPending}
      />
    </div>
  );

  return (
    <>
      <button
        type="button"
        data-testid="settings-backdrop"
        className="fixed inset-0 z-40 border-none bg-black/50"
        onClick={onClose}
        aria-label={t('quotes.closeDrawer')}
      />

      {isDesktop ? (
        <dialog
          open
          aria-label={t('contractor.settings')}
          className="fixed right-0 top-[57px] z-50 h-[calc(100vh-57px)] w-[400px] overflow-y-auto border-l border-border bg-card"
        >
          {header}
          {formContent}
        </dialog>
      ) : (
        <dialog
          open
          aria-label={t('contractor.settings')}
          className="fixed inset-x-0 bottom-0 z-50 max-h-[80vh] overflow-y-auto rounded-t-2xl border-t border-border bg-card"
        >
          {header}
          {formContent}
        </dialog>
      )}
    </>
  );
}
